import { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiResponse } from 'apisauce';

import { mergeIngestTitle, moveIngestPlaybackOption } from '@api/services/ingest';
import { movePlaybackOption, replaceMasterId, splitPlaybackOption } from '@api/services/masterId';
import useConfirmDialog from '@hooks/useConfirmDialog';
import { useProviderNames } from '@hooks/useProviderNames';
import useSearchContext from '@hooks/useSearchContext';
import useSnack from '@hooks/useSnack';
import { useIsNewIngest } from '@hooks/useTitlesDataSource';
import { DetailedPlaybackOption, Title } from '@models/Title';

import TitleSelector from '../components/TitleSelector';

import MasterIdManagerContext from './context';

export const MasterIdManagerContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentTitle, setCurrentTitle] = useState<Title>();
  const [currentPlaybackOption, setCurrentPlaybackOption] = useState<DetailedPlaybackOption>();
  const [loading, setLoading] = useState(false);

  const { addSnack } = useSnack();
  const { t } = useTranslation();
  const { getProviderName } = useProviderNames();
  const { country } = useSearchContext();
  const isNewIngest = useIsNewIngest();

  const { dialog, request } = useConfirmDialog(loading);

  const openMergeTitlesView = (title: Title) => setCurrentTitle(title);

  const openMovePlaybackOptionView = (title: Title, playbackOption: DetailedPlaybackOption) => {
    setCurrentTitle(title);
    setCurrentPlaybackOption(playbackOption);
  };

  function performMasterIdAction<T>(
    apiCall: () => Promise<ApiResponse<T>>,
    context: 'merge' | 'move' | 'split'
  ) {
    setLoading(true);

    apiCall()
      .then(({ ok, originalError }) => {
        if (originalError) throw originalError;
        if (!ok) throw new Error();
        addSnack(t(isNewIngest ? 'masterIdAction.successImmediate' : 'masterIdAction.success', { context }));
        setCurrentTitle(undefined);
        setCurrentPlaybackOption(undefined);
      })
      .catch(err => {
        console.error(err);
        addSnack(t('masterIdAction.error', { context }), { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }

  const handleOnSelectTitleToMerge = async (title: Title) =>
    currentTitle &&
    (await request(
      'Merge titles?',
      `The source title with master ID "${currentTitle.masterId}" will be deleted and replaced with the target title with master ID "${title.masterId}". Are you sure you want to continue?`,
      'yes'
    )) &&
    performMasterIdAction(
      () =>
        isNewIngest
          ? mergeIngestTitle(currentTitle.masterId, title.masterId, country)
          : replaceMasterId(currentTitle.masterId, title.masterId),
      'merge'
    );

  const closeTitleSelector = () => {
    setCurrentTitle(undefined);
    if (currentPlaybackOption) setCurrentPlaybackOption(undefined);
  };

  const handleOnSelectTitleToMovePlaybackOptionTo = (title: Title) => {
    if (!currentPlaybackOption || !currentTitle) closeTitleSelector();
    else
      performMasterIdAction(
        () =>
          isNewIngest
            ? moveIngestPlaybackOption(currentPlaybackOption.id, title.masterId, country)
            : movePlaybackOption(currentPlaybackOption.id, currentTitle.masterId, title.masterId),
        'move'
      );
  };

  const handleSplitIntoNewTitle = async (title: Title, playbackOption: DetailedPlaybackOption) =>
    (await request(
      'Split into new title',
      `Are you sure you wish to split the playback options provided by ${getProviderName(
        playbackOption.providerIdentifier
      )} into a new title?`,
      'yes'
    )) &&
    performMasterIdAction(
      () =>
        isNewIngest
          ? moveIngestPlaybackOption(playbackOption.id, null, country)
          : splitPlaybackOption(playbackOption.id, title.masterId),
      'split'
    );

  return (
    <MasterIdManagerContext.Provider
      value={{
        handleSplitIntoNewTitle,
        openMergeTitlesView,
        openMovePlaybackOptionView,
      }}
    >
      {children}

      <TitleSelector
        currentTitle={currentTitle}
        onDismiss={closeTitleSelector}
        show={!!currentTitle}
        type={currentTitle?.type ?? ''}
        {...(currentPlaybackOption
          ? {
              actionColor: 'info' as const,
              actionIcon: 'move' as const,
              actionLabel: t('move'),
              onSelect: handleOnSelectTitleToMovePlaybackOptionTo,
              heading: t('masterIdAction', { context: 'move' }),
            }
          : {
              actionColor: 'success' as const,
              actionIcon: 'merge' as const,
              actionLabel: t('merge'),
              onSelect: handleOnSelectTitleToMerge,
              heading: t('masterIdAction', {
                context: 'merge',
                titleName: currentTitle?.titleName,
              }),
            })}
      />

      {dialog}
    </MasterIdManagerContext.Provider>
  );
};
